import { useRouter } from '@tanstack/react-router';
import type { PlaceImportFromGmapsResponse } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { usePlaceImportFromGmaps } from '../api/place-import.mutations';

type PrefillData = Extract<PlaceImportFromGmapsResponse, { kind: 'prefill' }>;

type Props = {
  onPrefill: (data: PrefillData) => void;
};

export function PlaceImportField({ onPrefill }: Props) {
  const [url, setUrl] = useState('');
  const router = useRouter();
  const mut = usePlaceImportFromGmaps();

  const handleImport = async () => {
    try {
      const res = await mut.mutateAsync(url.trim());
      if (res.kind === 'existing') {
        toast.info('Это место уже есть в системе, открываю редактирование');
        router.navigate({ to: '/places/$placeId', params: { placeId: res.placeId } });
        return;
      }
      onPrefill(res);
      toast.success('Поля заполнены, проверьте и сохраните');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ошибка импорта');
    }
  };

  const disabled = !url.trim() || mut.isPending;

  return (
    <div className="mb-4 rounded-lg border bg-card p-4 shadow-sm">
      <div className="mb-3">
        <h3 className="text-base font-semibold leading-none tracking-tight">Импорт из Google Maps</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Вставьте ссылку — поля формы заполнятся автоматически
        </p>
      </div>
      <div className="flex gap-2">
        <Input
          placeholder="https://maps.app.goo.gl/... или https://google.com/maps/place/..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !disabled) {
              e.preventDefault();
              handleImport();
            }
          }}
          disabled={mut.isPending}
        />
        <Button onClick={handleImport} disabled={disabled} type="button">
          {mut.isPending ? <Loader2 className="animate-spin" /> : 'Импорт'}
        </Button>
      </div>
    </div>
  );
}
